import React, { useState, useEffect, useMemo, useRef } from 'react'
import styled from '@emotion/styled'
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet'
import { LatLngTuple } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { colors } from '../../../../../constants'
import { FlexContainer, YSpacing, DividerLine } from '@components/common'
import {
  GOScheduleMenu,
  DateMenuTemplate,
  Option,
  ShowInfoModalReq,
  GOClientLocation,
  Chef,
  GOClientSettings,
  Account,
} from '@types'
import { useChefsMap, AllChefsList } from './useChefsMap'
import { useDateMenusMap, SuggestionList } from './useDateMenusMap'
import { useMarketChefsMap, MarketChefsList } from './useMarketChefsMap'
import {
  CreateDateMenuTemplateReq,
  UpdateDateMenuTemplateReq,
  SearchDateMenuTemplatesReq,
} from '@coordinators/groupOrders/dateMenuTemplates'
import { SearchClientSettingsParams } from '../ClientLocationSelector'
// Set up Leaflet default icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

// Define display modes for the map
type MapDisplayMode = 'suggestions' | 'allMenus' | 'marketChefs'

interface MenuSuggestionsMapProps {
  hqId: number
  menus: GOScheduleMenu[]
  cuisineOptions: Option[]
  client: Account | null
  clientSettings: GOClientSettings | null
  clientLocation?: GOClientLocation
  clientName?: string
  withinBudget: boolean | null
  onChangeWithinBudget: (withinBudget: boolean) => void
  displayFailureMessage: (message: string) => void
  displayInfoMessage: (message: string) => void
  showInfoModal: (req: ShowInfoModalReq) => void
  closeInfoModal: () => void
  userId: string
  userEmail: string
  loadChefs: (params: any) => Promise<Chef[]>
  loadAccounts: (val: { search: string }) => Promise<Account[]>
  searchClientSettings: (
    params: SearchClientSettingsParams,
  ) => Promise<GOClientSettings[]>
  onSelectClientLocation: (
    account: Account,
    setting: GOClientSettings,
    location: GOClientLocation,
  ) => void
  displayConfirmationModal: ({
    title,
    text,
  }: {
    title: string
    text: string
  }) => Promise<boolean>
  createDateMenuTemplate: (
    req: CreateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  updateDateMenuTemplate: (
    req: UpdateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  searchDateMenuTemplates: (
    req: SearchDateMenuTemplatesReq,
  ) => Promise<DateMenuTemplate[] | null>
  deleteDateMenuTemplates: (id: string) => Promise<boolean>
}

const MenuSuggestionsMap: React.FC<MenuSuggestionsMapProps> = ({
  hqId,
  menus,
  cuisineOptions,
  client,
  clientSettings,
  clientLocation,
  clientName,
  withinBudget,
  onChangeWithinBudget,
  displayFailureMessage,
  displayInfoMessage,
  showInfoModal,
  closeInfoModal,
  userId,
  userEmail,
  loadChefs,
  loadAccounts,
  searchClientSettings,
  onSelectClientLocation,
  displayConfirmationModal,
  createDateMenuTemplate,
  updateDateMenuTemplate,
  searchDateMenuTemplates,
  deleteDateMenuTemplates,
}) => {
  const [displayMode, setDisplayMode] = useState<MapDisplayMode>('allMenus')

  // Use the useChefsMap hook to manage chef menu groups and filtering
  const {
    filteredChefMenuGroups,
    mapMarkers: chefMapMarkers,
    mapCenter: allChefsMapCenter,
    menusCount,
    searchTerm,
    setSearchTerm,
    includesCuisines,
    setIncludesCuisines,
    excludesCuisines,
    setExcludesCuisines,
    selectedChefId,
    handleSelectChef,
    includeAnchorChefs,
    setIncludeAnchorChefs,
  } = useChefsMap({
    hqId,
    menus,
    clientLocation,
    clientName,
    cuisineOptions,
  })

  // Use the useDateMenusMap hook to manage date menu suggestions
  const {
    dateMenuSuggestions,
    selectedDateMenuTemplate,
    setSelectedDateMenuTemplate,
    onGenerateDateMenuSuggestions,
    deadendTemplateIndex,
    selectedSuggestion,
    handleSelectSuggestion,
    handleClearSuggestions,
    handleImportCSV: handleDateMenusImportCSV,
    renderUnsuggestedChefMenuGroups,
    setRenderUnsuggestedChefMenuGroups,
    renderAllDateMenuPolylines,
    setRenderAllDateMenuPolylines,
    suggestedMenus,
    mapPolylines: dateMenuMapPolylines,
    mapCenter: dateMenuMapCenter,
    suggestionsMarkers,
  } = useDateMenusMap({
    hqId,
    menus,
    clientLocation,
    displayFailureMessage,
    displayInfoMessage,
  })

  // Use the useMarketChefsMap hook to manage market chef selection and suggestions
  const {
    mapCenter: marketChefsMapCenter,
    chefMenuGroups: allChefMenuGroups,
    filteredChefMenuGroups: marketChefGroups,
    searchTerm: marketChefsSearch,
    setSearchTerm: setMarketChefsSearch,
    includesCuisines: marketChefsIncludesCuisines,
    setIncludesCuisines: setMarketChefsIncludesCuisines,
    excludesCuisines: marketChefsExcludesCuisines,
    setExcludesCuisines: setMarketChefsExcludesCuisines,
    mapMarkers: marketChefMapMarkers,
    mapPolylines: marketChefMapPolylines,
    selectedChefIds,
    handleSelectChef: handleSelectMarketChef,
    selectedTemplate,
    setSelectedTemplate,
    handleClearSelections,
    handleGenerateSuggestions,
    hasGeneratedSuggestions,
    generatedSuggestions,
    groupedChefsSuggestions,
    handleSelectGroupedChefSuggestions,
    handleSelectDateMenuSuggestion,
    handleSelectSuggestionMenu,
    handleImportCSV: handleMarketChefsImportCSV,
    selectedSuggestionId,
    selectedGroupedChefSuggestions,
    selectedSuggestionMenuId,
    maxMiles,
    setMaxMiles,
    includeAnchorChefs: marketIncludeAnchorChefs,
    setIncludeAnchorChefs: setMarketIncludeAnchorChefs,
  } = useMarketChefsMap({
    hqId,
    menus,
    displayInfoMessage,
    displayFailureMessage,
  })

  const unsuggestedChefMenuGroups = useMemo(() => {
    return filteredChefMenuGroups.filter((group) =>
      group.menus.some((menu) => !suggestedMenus[menu.id]),
    )
  }, [filteredChefMenuGroups, suggestedMenus])

  // Handle display mode change
  const handleDisplayModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMode = e.target.value as MapDisplayMode
    setDisplayMode(newMode)

    // Reset polylines when switching to all menus view
    if (newMode === 'allMenus') {
      setRenderAllDateMenuPolylines(false)
    }
  }

  // Get the appropriate markers based on display mode
  const mapMarkers = useMemo(() => {
    if (displayMode === 'suggestions') {
      // Start with client marker (always included in chefMapMarkers)
      const markers = [
        ...chefMapMarkers.filter((marker) => marker.id === 'client'),
      ]

      // Add suggestion markers if a suggestion is selected
      markers.push(...suggestionsMarkers)

      // Add unsuggested chef markers if enabled
      if (renderUnsuggestedChefMenuGroups) {
        markers.push(
          ...chefMapMarkers.filter(
            (marker) =>
              marker.id !== 'client' &&
              unsuggestedChefMenuGroups.some((group) =>
                marker.id.includes(group.chef.id),
              ),
          ),
        )
      }

      return markers
    } else if (displayMode === 'marketChefs') {
      // Use the markers from useMarketChefsMap for market chefs view
      return marketChefMapMarkers
    } else {
      // Use the markers from useChefsMap for all menus view
      return chefMapMarkers
    }
  }, [
    displayMode,
    chefMapMarkers,
    marketChefMapMarkers,
    suggestionsMarkers,
    renderUnsuggestedChefMenuGroups,
    unsuggestedChefMenuGroups,
  ])

  // Get the appropriate polylines based on display mode
  const mapPolylines = useMemo(() => {
    if (displayMode === 'suggestions') {
      return dateMenuMapPolylines
    } else if (displayMode === 'marketChefs') {
      return marketChefMapPolylines
    }

    return []
  }, [displayMode, dateMenuMapPolylines, marketChefMapPolylines])

  // Render correct content based on display mode
  const renderSidebarContent = () => {
    if (displayMode === 'suggestions') {
      return (
        <SuggestionList
          hqId={hqId}
          userId={userId}
          userEmail={userEmail}
          loadChefs={loadChefs}
          client={client}
          clientSettings={clientSettings}
          withinBudget={withinBudget}
          onChangeWithinBudget={onChangeWithinBudget}
          loadAccounts={loadAccounts}
          searchClientSettings={searchClientSettings}
          onSelectClientLocation={onSelectClientLocation}
          displayFailureMessage={displayFailureMessage}
          displayInfoMessage={displayInfoMessage}
          displayConfirmationModal={displayConfirmationModal}
          createDateMenuTemplate={createDateMenuTemplate}
          updateDateMenuTemplate={updateDateMenuTemplate}
          searchDateMenuTemplates={searchDateMenuTemplates}
          deleteDateMenuTemplates={deleteDateMenuTemplates}
          showInfoModal={showInfoModal}
          closeInfoModal={closeInfoModal}
          cuisineOptions={cuisineOptions}
          menusCount={menusCount}
          selectedDateMenuTemplate={selectedDateMenuTemplate}
          setSelectedDateMenuTemplate={setSelectedDateMenuTemplate}
          onGenerateDateMenuSuggestions={onGenerateDateMenuSuggestions}
          dateMenuSuggestions={dateMenuSuggestions}
          deadendTemplateIndex={deadendTemplateIndex}
          numUnsuggestedChefs={unsuggestedChefMenuGroups.length}
          selectedSuggestion={selectedSuggestion}
          handleSelectSuggestion={handleSelectSuggestion}
          handleClearSuggestions={handleClearSuggestions}
          renderUnsuggestedChefMenuGroups={renderUnsuggestedChefMenuGroups}
          renderAllDateMenuPolylines={renderAllDateMenuPolylines}
          handleImportCSV={handleDateMenusImportCSV}
          setRenderUnsuggestedChefMenuGroups={
            setRenderUnsuggestedChefMenuGroups
          }
          setRenderAllDateMenuPolylines={setRenderAllDateMenuPolylines}
        />
      )
    } else if (displayMode === 'marketChefs') {
      return (
        <MarketChefsList
          hqId={hqId}
          userId={userId}
          userEmail={userEmail}
          loadChefs={loadChefs}
          displayFailureMessage={displayFailureMessage}
          displayInfoMessage={displayInfoMessage}
          displayConfirmationModal={displayConfirmationModal}
          createDateMenuTemplate={createDateMenuTemplate}
          updateDateMenuTemplate={updateDateMenuTemplate}
          searchDateMenuTemplates={searchDateMenuTemplates}
          deleteDateMenuTemplates={deleteDateMenuTemplates}
          chefMenuGroups={marketChefGroups}
          allChefMenuGroups={allChefMenuGroups}
          menus={menus}
          maxMiles={maxMiles}
          setMaxMiles={setMaxMiles}
          selectedChefIds={selectedChefIds}
          selectedTemplate={selectedTemplate}
          selectedSuggestionId={selectedSuggestionId}
          selectedGroupedChefSuggestions={selectedGroupedChefSuggestions}
          selectedSuggestionMenuId={selectedSuggestionMenuId}
          onSelectChef={handleSelectMarketChef}
          onSelectTemplate={setSelectedTemplate}
          onClearSelections={handleClearSelections}
          onGenerateSuggestions={handleGenerateSuggestions}
          hasGeneratedSuggestions={hasGeneratedSuggestions}
          generatedSuggestions={generatedSuggestions}
          groupedChefsSuggestions={groupedChefsSuggestions}
          handleSelectGroupedChefSuggestions={
            handleSelectGroupedChefSuggestions
          }
          handleSelectDateMenuSuggestion={handleSelectDateMenuSuggestion}
          handleSelectSuggestionMenu={handleSelectSuggestionMenu}
          handleImportCSV={handleMarketChefsImportCSV}
          searchTerm={marketChefsSearch}
          setSearchTerm={setMarketChefsSearch}
          includesCuisines={marketChefsIncludesCuisines}
          setIncludesCuisines={setMarketChefsIncludesCuisines}
          excludesCuisines={marketChefsExcludesCuisines}
          setExcludesCuisines={setMarketChefsExcludesCuisines}
          cuisineOptions={cuisineOptions}
          includeAnchorChefs={marketIncludeAnchorChefs}
          setIncludeAnchorChefs={setMarketIncludeAnchorChefs}
          showInfoModal={showInfoModal}
          closeInfoModal={closeInfoModal}
        />
      )
    } else {
      return (
        <AllChefsList
          chefMenuGroups={filteredChefMenuGroups}
          menusCount={menusCount}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          includesCuisines={includesCuisines}
          setIncludesCuisines={setIncludesCuisines}
          excludesCuisines={excludesCuisines}
          setExcludesCuisines={setExcludesCuisines}
          cuisineOptions={cuisineOptions}
          selectedChefId={selectedChefId}
          includeAnchorChefs={includeAnchorChefs}
          setIncludeAnchorChefs={setIncludeAnchorChefs}
          onSelectChef={handleSelectChef}
          showInfoModal={showInfoModal}
        />
      )
    }
  }

  // Render the sidebar with suggestion options
  const renderSidebar = () => {
    return (
      <SideBar>
        <FlexContainer
          flexDirection="column"
          alignItems="center"
          padding="10px"
        >
          <h2>Map</h2>
          <YSpacing height="10px" />

          {/* Display mode selector */}
          <FlexContainer flexDirection="column" width="100%">
            <InputLabel htmlFor="displayMode">Display Mode:</InputLabel>
            <Select
              id="displayMode"
              value={displayMode}
              onChange={handleDisplayModeChange}
            >
              <option value="allMenus">All Available Menus</option>
              <option value="suggestions">Client Menus</option>
              <option value="marketChefs">Market Menus</option>
            </Select>
          </FlexContainer>

          <YSpacing height="20px" />
          <DividerLine />
          <YSpacing height="20px" />

          {renderSidebarContent()}
        </FlexContainer>
      </SideBar>
    )
  }

  // Render the map with markers and polyline
  const renderMap = () => {
    let mapCenter: LatLngTuple | null = null
    if (displayMode === 'allMenus') {
      mapCenter = allChefsMapCenter
    } else if (displayMode === 'marketChefs') {
      mapCenter = marketChefsMapCenter
    } else if (displayMode === 'suggestions') {
      mapCenter = dateMenuMapCenter
    }

    return (
      <MapContainer
        center={mapCenter || [38.8817965, -77.1169779]}
        zoom={11}
        style={{ height: '100%', width: '100%' }}
      >
        <MapEffects mapCenter={mapCenter} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Render all markers */}
        {mapMarkers.map((marker) => (
          <Marker key={marker.id} position={marker.position} icon={marker.icon}>
            <Popup>{marker.popupContent}</Popup>
          </Marker>
        ))}

        {/* Render polylines */}
        {mapPolylines.map((polyline, index) => (
          <Polyline
            key={index}
            positions={polyline.positions}
            pathOptions={polyline.pathOptions}
          />
        ))}
      </MapContainer>
    )
  }

  return (
    <Container>
      {renderSidebar()}
      <MapWrapper>{renderMap()}</MapWrapper>
    </Container>
  )
}

interface MapEffectsProps {
  mapCenter: LatLngTuple | null
}

const MapEffects: React.FC<MapEffectsProps> = ({
  mapCenter,
}: {
  mapCenter: LatLngTuple | null
}) => {
  const previousMapCenterRef = useRef<LatLngTuple | null>(null)
  const map = useMap()

  useEffect(() => {
    if (
      mapCenter &&
      (previousMapCenterRef.current === null ||
        previousMapCenterRef.current[0] !== mapCenter[0] ||
        previousMapCenterRef.current[1] !== mapCenter[1])
    ) {
      map.panTo(mapCenter)
      previousMapCenterRef.current = mapCenter
    }
  }, [mapCenter, map])

  return null
}

// Styled components
const Container = styled.div`
  display: flex;
  height: 1000px;
  width: 100%;
  font-family: 'Inter', sans-serif;
`

// New wrapper for the map to handle flex styling
const MapWrapper = styled.div`
  flex: 1;
  height: 100%;
  min-width: 0;
`

const SideBar = styled.div`
  width: 350px;
  min-width: 350px;
  height: 100%;
  overflow-y: auto;
  border-right: 1px solid ${colors.gray200};
  background-color: white;
  padding: 20px;
  box-sizing: border-box;

  h2 {
    font-size: 1.4em;
    font-weight: 600;
    color: ${colors.dark500};
    margin-bottom: 15px;
    text-align: center;
    width: 100%;
  }

  h3 {
    font-size: 1.1em;
    font-weight: 600;
    color: ${colors.dark500};
    margin-top: 15px;
    margin-bottom: 10px;
    width: 100%;
    text-align: left;
  }

  p {
    font-size: 0.95em;
    color: ${colors.gray400};
    line-height: 1.4;
    margin-bottom: 10px;
  }
`

const InputLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.9em;
  font-weight: bold;
  color: ${colors.dark500};
`

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  background-color: white;
  font-size: 0.95em;
  box-sizing: border-box;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 10px top 50%;
  background-size: 0.65em auto;
  padding-right: 30px;

  &:focus {
    border-color: ${colors.blue400};
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue100};
  }
`

export default MenuSuggestionsMap
